import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import '../../App.css';
import Button from '../../components/ui/Button';
import { ConfirmModal } from '../../components/ui';
import ActivityForm, { IActivityFormData } from '../../components/forms/ActivityForm';
import DataTable, { Column } from '../../components/DataTable';
import Modal from '../../components/Modal';
import MultiSelect, { SelectId } from '../../components/MultiSelect';
import RoleAccessWidget from '../../components/RoleAccessWidget';
import EditColumns from '../../components/EditColumns';
import { translateError } from '../../utils/errorMessages';

interface IActivity {
  _id: string;
  name: string;
  description: string;
  visibility: 'public' | 'internal' | 'hidden';
  joinPolicy: 'open' | 'approval';
  sortOrder: number;
  coverImage?: string | null;
  /** null = payments collect into the organization's default account. */
  bankAccountId: number | null;
  groupIds: number[];
  templates: { _id: string | number; name: string }[];
  members: { _id: string | number; name: string }[];
  memberships: { _id: string | number; memberId: number; memberName: string; joinedAt: string }[];
}

interface IMember {
  _id: string;
  name: string;
  description: string;
  user?: {
    _id: string;
    name: string;
    email: string;
  };
}

interface IGroup {
  _id: string;
  name: string;
}

interface ICourseSummary {
  _id: string | number;
  name: string;
  enrollmentCount?: number;
}

const ActivityEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { hasPermission } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [activity, setActivity] = useState<IActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Member management state
  const [allMembers, setAllMembers] = useState<IMember[]>([]);
  const [allGroups, setAllGroups] = useState<IGroup[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showAddByGroupModal, setShowAddByGroupModal] = useState(false);
  const [addMemberIds, setAddMemberIds] = useState<SelectId[]>([]);
  const [addGroupId, setAddGroupId] = useState<SelectId[]>([]);
  const [addMembersLoading, setAddMembersLoading] = useState(false);
  const [removeMemberId, setRemoveMemberId] = useState<string | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Courses belonging to this activity (read-only summary list).
  const [courses, setCourses] = useState<ICourseSummary[]>([]);

  // Enrollment writes need both rights (see activityRoutes member routes).
  const canManageMembers = hasPermission('activities:write') && hasPermission('members:write');

  const navigate = useNavigate();

  const fetchActivity = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.activities.get(Number(id));
      setActivity(data as IActivity);
      setLoading(false);
    } catch (err: any) {
      setError(translateError(err, t));
      setLoading(false);
      if (err.response && err.response.status === 404) {
        navigate('/admin/activities');
      }
    }
  }, [id, navigate]);

  const fetchOptions = useCallback(async () => {
    setMembersLoading(true);
    try {
      const [membersRes, groupsRes] = await Promise.all([
        api.members.list(),
        api.groups.list(),
      ]);
      setAllMembers(membersRes.data as IMember[]);
      setAllGroups(groupsRes.data as IGroup[]);
    } catch {
      // Non-critical, silently fail
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.courses.list({ activityId: Number(id) });
      setCourses(data as ICourseSummary[]);
    } catch {
      // Non-critical — courses section just stays empty.
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchActivity();
      fetchOptions();
      fetchCourses();
    }
  }, [id, fetchActivity, fetchOptions, fetchCourses]);

  // Members currently in the activity (from the detail endpoint)
  const activityMemberIds = useMemo(() => {
    if (!activity?.memberships) return new Set<string>();
    return new Set(activity.memberships.map(m => String(m.memberId)));
  }, [activity]);

  const membersInActivity = useMemo(() => {
    return allMembers.filter(m => activityMemberIds.has(String(m._id)));
  }, [allMembers, activityMemberIds]);

  const membersNotInActivity = useMemo(() => {
    return allMembers.filter(m => !activityMemberIds.has(String(m._id)));
  }, [allMembers, activityMemberIds]);

  const memberColumns: Column<IMember>[] = useMemo(() => [
    { header: t('members.name'), key: 'name' },
    { header: t('common.description'), key: 'description' },
    {
      header: t('members.userCaregiver'),
      render: (member) => member.user ? member.user.name : 'N/A',
    },
  ], [t]);

  // Build initial form data from the activity
  const initialFormData = useMemo<Partial<IActivityFormData> | undefined>(() => {
    if (!activity) return undefined;
    return {
      name: activity.name,
      description: activity.description,
      visibility: activity.visibility,
      joinPolicy: activity.joinPolicy,
      sortOrder: activity.sortOrder,
      coverImage: activity.coverImage || '',
      bankAccountId: activity.bankAccountId ?? null,
      groupIds: activity.groupIds?.map(id => id) || [],
      templateIds: activity.templates?.map(t => t._id) || [],
    };
  }, [activity]);

  const submitHandler = async (data: IActivityFormData) => {
    setLoading(true);
    setError(null);

    try {
      await api.activities.update(Number(id), {
        name: data.name,
        description: data.description,
        visibility: data.visibility,
        joinPolicy: data.joinPolicy,
        sortOrder: data.sortOrder,
        coverImage: data.coverImage || null,
        bankAccountId: data.bankAccountId,
        groupIds: data.groupIds,
        templateIds: data.templateIds,
      });
      setLoading(false);
      addToast(t('activities.updated'), 'success');
      navigate('/admin/activities');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setLoading(false);
    }
  };

  const handleAddMembers = async () => {
    if (!id || addMemberIds.length === 0) return;
    setAddMembersLoading(true);
    try {
      await api.activities.addMembers(Number(id), {
        memberIds: addMemberIds.map(Number),
      });
      addToast(t('activities.addMembersSuccess'), 'success');
      setShowAddMembersModal(false);
      setAddMemberIds([]);
      fetchActivity();
      fetchOptions();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setAddMembersLoading(false);
    }
  };

  const handleAddByGroup = async () => {
    if (!id || addGroupId.length === 0) return;
    setAddMembersLoading(true);
    try {
      await api.activities.addMembers(Number(id), {
        groupId: Number(addGroupId[0]),
      });
      addToast(t('activities.addMembersSuccess'), 'success');
      setShowAddByGroupModal(false);
      setAddGroupId([]);
      fetchActivity();
      fetchOptions();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setAddMembersLoading(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!id || !removeMemberId) return;
    setRemoveLoading(true);
    try {
      await api.activities.removeMember(Number(id), Number(removeMemberId));
      addToast(t('activities.removeMemberSuccess'), 'success');
      setRemoveMemberId(null);
      fetchActivity();
      fetchOptions();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setRemoveLoading(false);
    }
  };

  if (loading && !activity) return <p>{t('activities.loadingActivity')}</p>;
  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  // Auxiliary sections live in the folding side column — beside the form on
  // desktop, a "Details" tab on mobile. Each is its own widget card; admin
  // access sits last.
  const sideContent = (
    <div className="edit-widgets-stack">
      {/* Members Section */}
      <div className="edit-widget-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ margin: 0 }}>{t('activities.members')}</h3>
          {canManageMembers && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button variant="secondary" size="sm" onClick={() => { setAddGroupId([]); setShowAddByGroupModal(true); }}>
                {t('activities.addByGroup')}
              </Button>
              <Button variant="primary" size="sm" onClick={() => { setAddMemberIds([]); setShowAddMembersModal(true); }}>
                {t('activities.addMembers')}
              </Button>
            </div>
          )}
        </div>

        {membersLoading ? (
          <p>{t('common.loading')}</p>
        ) : membersInActivity.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>{t('activities.noActivityMembers')}</p>
        ) : (
          <DataTable
            data={membersInActivity}
            columns={memberColumns}
            onDelete={canManageMembers ? (member) => setRemoveMemberId(member._id) : undefined}
          />
        )}
      </div>

      {/* Courses Section */}
      {id && (
        <div className="edit-widget-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>{t('courses.title')}</h3>
            <Link to={`/admin/courses/create?activityId=${id}`} className="btn btn--primary btn--sm">
              {t('courses.create')}
            </Link>
          </div>

          {courses.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>{t('courses.noCourses')}</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {courses.map((course) => (
                <li
                  key={course._id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.6rem 0',
                    borderBottom: '1px solid var(--border-color)',
                    gap: '0.5rem',
                  }}
                >
                  <span>
                    {course.name}
                    <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                      {t('courses.enrolledCount', { count: course.enrollmentCount ?? 0 })}
                    </span>
                  </span>
                  <Link to={`/admin/courses/${course._id}/edit`} className="btn btn--secondary btn--sm">
                    {t('common.edit')}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Admin access (scoped roles with access to this activity) — last. */}
      {id && (
        <div className="edit-widget-card">
          <h3 style={{ marginBottom: '0.25rem', marginTop: 0 }}>{t('activities.adminAccess')}</h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: '0.75rem' }}>
            {t('activities.adminAccessHelp')}
          </p>
          <RoleAccessWidget entityType="activities" entityId={id} />
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-page-content">
      <h2>{t('activities.edit')}</h2>
      <EditColumns
        mainLabel={t('activities.paneForm')}
        sideLabel={t('activities.paneDetails')}
        side={sideContent}
      >
        <ActivityForm
          initialData={initialFormData}
          seedKey={activity?._id}
          onSubmit={submitHandler}
          loading={loading && !!activity}
          submitButtonText={t('activities.updateButton')}
          activityId={id ? Number(id) : undefined}
        />
        <Link to="/admin/activities" className="form-link">{t('activities.goBack')}</Link>
      </EditColumns>

      {/* Add Members Modal */}
      <Modal
        isOpen={showAddMembersModal}
        onClose={() => { setShowAddMembersModal(false); setAddMemberIds([]); }}
        title={t('activities.addMembersTitle')}
        size="medium"
      >
        <div style={{ padding: '0.5rem 0' }}>
          <MultiSelect
            options={membersNotInActivity.map(m => ({ _id: m._id, name: m.name }))}
            selectedIds={addMemberIds}
            onChange={setAddMemberIds}
            placeholder={t('members.name')}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
            <Button
              variant="secondary"
              onClick={() => { setShowAddMembersModal(false); setAddMemberIds([]); }}
              disabled={addMembersLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAddMembers}
              loading={addMembersLoading}
              disabled={addMemberIds.length === 0}
            >
              {t('activities.addMembers')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add by Group Modal */}
      <Modal
        isOpen={showAddByGroupModal}
        onClose={() => { setShowAddByGroupModal(false); setAddGroupId([]); }}
        title={t('activities.addByGroup')}
        size="medium"
      >
        <div style={{ padding: '0.5rem 0' }}>
          <MultiSelect
            options={allGroups.map(g => ({ _id: g._id, name: g.name }))}
            selectedIds={addGroupId}
            onChange={(ids) => setAddGroupId(ids.length > 0 ? [ids[ids.length - 1]] : [])}
            placeholder={t('activities.groups')}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
            <Button
              variant="secondary"
              onClick={() => { setShowAddByGroupModal(false); setAddGroupId([]); }}
              disabled={addMembersLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAddByGroup}
              loading={addMembersLoading}
              disabled={addGroupId.length === 0}
            >
              {t('activities.addByGroup')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remove Member Confirmation */}
      <ConfirmModal
        isOpen={!!removeMemberId}
        onClose={() => setRemoveMemberId(null)}
        onConfirm={handleRemoveMember}
        title={t('activities.removeMember')}
        message={t('activities.confirmDelete')}
        confirmText={t('activities.removeMember')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={removeLoading}
      />
    </div>
  );
};

export default ActivityEditPage;
